import Phaser from 'phaser';
import type { TowerType } from '../../core/types';
import { LEVELS } from '../../config/levels';
import { availableTowers, rosterThroughLevel, menuTowerTexture } from '../../config/unlocks';
import { TOWER_NAMES } from '../../config/displayNames';
import { POTIONS, type PotionKind } from '../../config/potions';
import { FUNGI_ITEMS, isFungiItemKind, type FungiItemKind } from '../../config/fungiItems';
import { canApply, formatRemaining } from '../../config/effects';
import { drawStarfield } from '../starfield';
import { installDebugReturn } from '../debugMode';
import { GOLD, GOLD_HEX, hubProgress } from '../hubShared';
import { ScrollList } from '../../ui/scrollList';

/** What the picker can put on a tower: a targeted potion or a Fungi item. */
export type PickKind = PotionKind | FungiItemKind;

// A 7-column grid of 120px boxes, scrolled inside a viewport with a bar on the
// right (spec 2026-10-24): the roster outgrew the three rows that used to fit.
const COLS = 7;
const BOX = 120;
const GAP = 16;
const ROW_PITCH = BOX + 14 + GAP;
const GRID_W = COLS * BOX + (COLS - 1) * GAP;
const GRID_X = 640 - GRID_W / 2;
const GRID_TOP = 116;
const GRID_H = 552;

/**
 * TowerPickScene — "which tower does this go on?" A grid of every tower the
 * player can field; the ones it cannot take (COLA on the Ninja Hambi, a tower
 * already carrying the same effect) are greyed with a reason. Picking starts
 * the effect and returns to `returnTo`; CANCEL keeps the potion or item by
 * putting it back in Storage instead. The grid scrolls — wheel, touchpad,
 * the bar on the right, or a drag.
 *
 * Scene data: { kind, returnTo, returnData? }.
 */
export class TowerPickScene extends Phaser.Scene {
  private kind: PickKind = 'pickle';
  private returnTo = 'HubScene';
  private returnData: object = {};
  private done = false;

  constructor() {
    super({ key: 'TowerPickScene' });
  }

  init(data: { kind?: PickKind; returnTo?: string; returnData?: object }): void {
    this.kind = data?.kind ?? 'pickle';
    this.returnTo = data?.returnTo ?? 'HubScene';
    this.returnData = data?.returnData ?? {};
  }

  /** Back on the shelf — CANCEL, or a pick the rules refused after all. */
  private putBack(): void {
    const progress = hubProgress();
    if (isFungiItemKind(this.kind)) progress.awardFungiItem(this.kind);
    else progress.storePotion(this.kind);
  }

  create(): void {
    this.done = false;
    const progress = hubProgress();
    const item = isFungiItemKind(this.kind);
    const def = item ? FUNGI_ITEMS[this.kind as FungiItemKind] : POTIONS[this.kind as PotionKind];
    const now = Date.now();
    const effects = progress.effects();

    drawStarfield(this, 1280, 720);
    this.add
      .text(640, 40, `USE ${def.name.toUpperCase()}`, {
        fontSize: '34px',
        color: item ? '#f0a860' : '#7ed67e',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName('towerpick-title');
    this.add
      .text(640, 80, `${def.effect} · lasts ${formatRemaining(def.durationMs)}. Pick the tower.`, {
        fontSize: '16px',
        color: '#c8d2d8',
      })
      .setOrigin(0.5);

    const cancel = this.add
      .text(80, 50, '✕ CANCEL', {
        fontSize: '20px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 14, y: 12 },
      })
      .setOrigin(0.5)
      .setName('towerpick-cancel')
      .setDepth(20)
      .setInteractive({ useHandCursor: true });
    cancel.on('pointerover', () => cancel.setColor(GOLD_HEX));
    cancel.on('pointerout', () => cancel.setColor('#cfe0bd'));
    cancel.on('pointerup', () => {
      if (this.done) return;
      this.done = true;
      // Not lost: it goes on the shelf instead.
      this.putBack();
      this.scene.start(this.returnTo, this.returnData);
    });
    this.add
      .text(640, 700, `CANCEL keeps the ${item ? 'item' : 'potion'} in Storage.`, {
        fontSize: '14px',
        color: '#9aa6ad',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);

    const roster = availableTowers(progress.enteredLevels(), progress.unlockedExtras());
    const towers: TowerType[] = roster.length ? roster : rosterThroughLevel(LEVELS[0].id);

    const list = new ScrollList(this, {
      x: GRID_X,
      y: GRID_TOP,
      width: GRID_W,
      height: GRID_H,
      barX: GRID_X + GRID_W + 22,
      barWidth: 12,
      depth: 1,
      cullText: true,
    });

    towers.forEach((type, i) => {
      const x = (i % COLS) * (BOX + GAP) + BOX / 2;
      const y = Math.floor(i / COLS) * ROW_PITCH + (BOX + 14) / 2 + 4;
      const check = canApply(effects, this.kind, type, now);
      const ok = check.ok;
      const box = this.add
        .rectangle(x, y, BOX, BOX + 14, 0x15131f, ok ? 0.9 : 0.5)
        .setStrokeStyle(ok ? 2 : 1, ok ? GOLD : 0x555a66, ok ? 0.8 : 0.5)
        .setName(`towerpick-${type}`);
      list.content.add(box);
      const art = this.textures.exists(menuTowerTexture(type))
        ? menuTowerTexture(type)
        : 'tower-patty-1';
      const icon = this.add.image(x, y - 12, art).setDisplaySize(72, 72);
      if (!ok) icon.setAlpha(0.35);
      list.content.add(icon);
      list.content.add(
        this.add
          .text(x, y + 38, TOWER_NAMES[type], {
            fontSize: '11px',
            color: ok ? '#e8ecef' : '#6a7078',
            align: 'center',
            wordWrap: { width: BOX - 8 },
          })
          .setOrigin(0.5),
      );
      if (!ok) {
        list.content.add(
          this.add
            .text(x, y + 56, check.ok ? '' : check.reason, {
              fontSize: '9px',
              color: '#c08a3e',
              align: 'center',
              wordWrap: { width: BOX - 6 },
            })
            .setOrigin(0.5),
        );
        return;
      }
      box.setInteractive({ useHandCursor: true });
      box.on('pointerover', () => box.setStrokeStyle(3, GOLD, 1));
      box.on('pointerout', () => box.setStrokeStyle(2, GOLD, 0.8));
      box.on('pointerup', (p: Phaser.Input.Pointer) => {
        // The end of a scroll flick is not a tap, and a box scrolled out of
        // the viewport still takes input under its mask.
        if (this.done || list.dragged || !list.inView(p)) return;
        const r = progress.startEffect(this.kind, type, Date.now());
        if (!r.ok) {
          // The rules changed under us (another device, a clock jump): shelve it.
          this.putBack();
        }
        this.done = true;
        this.scene.start(this.returnTo, this.returnData);
      });
    });
    list.setContentHeight(Math.ceil(towers.length / COLS) * ROW_PITCH);

    installDebugReturn(this);
  }
}
